import { z } from 'zod';

const TerminologyTermItem = z.object({
  id: z.uuid(),
  termName: z.string(),
  category: z.string().nullable(),
  status: z.boolean(),
  brandId: z.number().nullable(),
});

const terminologyListItemSchema = TerminologyTermItem.extend({
  aliasCount: z.number().int(),
  disambiguationCount: z.number().int(),
});

export type TerminologyTermListItem = z.infer<typeof terminologyListItemSchema>;

const TerminologyAliasItem = z.object({
  id: z.uuid(),
  alias: z.string(),
});

const TerminologyDisambiguationItem = z.object({
  id: z.uuid(),
  sttInput: z.string(),
});

export namespace ListTerminologyTerms {
  export const Schema = {
    Query: z.object({
      skip: z.coerce.number().int().min(0).default(0),
      limit: z.coerce.number().int().min(1).max(200).default(100),
    }),
    Response: z.object({
      items: z.array(terminologyListItemSchema),
      hasMore: z.boolean(),
    }),
  };

  export type Request = z.infer<typeof Schema.Query>;
  export type Response = z.infer<typeof Schema.Response>;
}

export namespace CreateTerminologyTerm {
  export const Schema = {
    Body: z.object({
      termName: z.string().trim().min(1),
      category: z.string().trim().min(1).optional(),
      brandId: z.number().nullable().optional(),
      aliases: z
        .array(z.string().trim().min(1))
        .refine((items) => new Set(items.map((s) => s.toLowerCase())).size === items.length, {
          message: 'Aliases must be unique',
        })
        .default([]),
      disambiguations: z
        .array(z.string().trim().min(1))
        .refine((items) => new Set(items.map((s) => s.toLowerCase())).size === items.length, {
          message: 'Disambiguations must be unique',
        })
        .default([]),
    }),
    Response: TerminologyTermItem,
  };

  export type Request = z.infer<typeof Schema.Body>;
  export type Response = z.infer<typeof Schema.Response>;
}

export namespace DeleteTerminologyTerm {
  export const Schema = {
    Params: z.object({ id: z.uuid() }),
  };
}

export namespace UpdateTerminologyTermFull {
  export const Schema = {
    Params: z.object({ id: z.uuid() }),
    Body: z.object({
      termName: z.string().trim().min(1),
      category: z.string().trim().min(1).nullable().optional(),
      brandId: z.number().nullable().optional(),
      status: z.boolean(),
      aliases: z
        .array(z.string().trim().min(1))
        .refine((items) => new Set(items.map((s) => s.toLowerCase())).size === items.length, {
          message: 'Aliases must be unique',
        }),
      disambiguations: z
        .array(z.string().trim().min(1))
        .refine((items) => new Set(items.map((s) => s.toLowerCase())).size === items.length, {
          message: 'Disambiguations must be unique',
        }),
    }),
    Response: TerminologyTermItem,
  };

  export type Request = z.infer<typeof Schema.Body>;
  export type Response = z.infer<typeof Schema.Response>;
}

export namespace ListTerminologyAliases {
  export const Schema = {
    Params: z.object({ id: z.uuid() }),
    Response: z.object({
      items: z.array(TerminologyAliasItem),
    }),
  };

  export type Response = z.infer<typeof Schema.Response>;
}

export namespace ListTerminologyDisambiguations {
  export const Schema = {
    Params: z.object({ id: z.uuid() }),
    Response: z.object({
      items: z.array(TerminologyDisambiguationItem),
    }),
  };

  export type Response = z.infer<typeof Schema.Response>;
}

export const TERMINOLOGY_CATEGORIES = [
  'Brand',
  'Metrics',
  'Access',
  'Roles',
  'Distribution',
  'Therapeutic Area',
] as const;

export const terminologyCategorySchema = z.enum(TERMINOLOGY_CATEGORIES);
export type TerminologyCategory = z.infer<typeof terminologyCategorySchema>;

export const TERMINOLOGY_SCOPES = ['global', 'brand'] as const;
export const terminologyScopeSchema = z.enum(TERMINOLOGY_SCOPES);
export type TerminologyScope = z.infer<typeof terminologyScopeSchema>;

export namespace ExportTerminology {
  export const Schema = {
    Response: z.object({
      items: z.array(
        z.object({
          termName: z.string(),
          category: z.string().nullable(),
          scope: terminologyScopeSchema,
          brandId: z.number().nullable(),
          brandName: z.string().nullable(),
          aliases: z.array(z.string()),
          disambiguations: z.array(z.string()),
        })
      ),
    }),
  };

  export type Response = z.infer<typeof Schema.Response>;
}

export namespace BulkImportTerminology {
  export const Schema = {
    Body: z.object({
      rows: z.array(
        z.object({
          term: z.string().trim().min(1),
          aliases: z.array(z.string()),
          disambiguations: z.array(z.string()),
          category: terminologyCategorySchema.nullable().optional(),
          scope: terminologyScopeSchema.optional(),
          brandId: z.number().nullable().optional(),
          brandName: z.string().trim().min(1).optional(),
        })
      ),
    }),
    Response: z.object({
      imported: z.number().int(),
      skipped: z.array(z.string()),
    }),
  };

  export type Request = z.infer<typeof Schema.Body>;
  export type Response = z.infer<typeof Schema.Response>;
}
